import React from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import useSWR from 'swr';
import {subYears} from 'date-fns';

import {PATHS} from 'Routes';
import api, {getURL} from 'api';
import {useReservation} from 'context/reservation';
import {getHousingCountryCode} from 'utils/reservation';
import {GENDERS_OPTIONS, PATTERNS} from 'utils/constants';
import {getDocTypes, getRemappedDocType} from 'utils/docTypes';
import {
  useAbortController,
  useErrorModal,
  useIsMounted,
  useModalControls,
} from 'utils/hooks';
import {GlobalLocationTypes} from '../../../utils/types';
import {LocationStates} from 'views/VerifyModalScreen/VerifyModalScreen';

import successIcon from 'assets/success-icon.svg';
import scanningIcon from 'assets/scanning_doc_icn.svg';
import reviewDataIcon from 'assets/review-data-icon.svg';
import likeIcon from 'assets/like.svg';
import eyeIcon from 'assets/eye-white.svg';
import contractIcon from 'assets/contract.svg';

import Modal from 'components/Modal';
import {ModalButton, ModalSecondaryButton} from 'styled/common';
import {EyeIcon, ErrorModalFirstButton, ContractIcon} from './styled';
import {ConfirmIdentityTemplate} from 'components/ConfirmIdentity';

const SUCCESS_MODAL_TIMEOUT_S = 1.5;
const DETECTING_RESPONSES_GAP_S = 1;
const MAX_SCAN_FAILURE_ATTEMPTS_BEFORE_FORM_PROCEED = 3;

enum DETECTING_STATUSES {
  completed = 'COM',
  error = 'ERR',
  processing = 'PRO',
  new = 'NEW',
}

export type BackLocationStateType = GlobalLocationTypes & {
  country: {
    code: string;
    name: string;
  };
};

function getLocationsAsOptions(data: {results: Array<BackLocationStateType>}) {
  const results = data?.results;

  if (!results) {
    return [];
  }
  return results.map(l => {
    return {
      value: l?.country?.code,
      label: l?.country?.name,
    };
  });
}

function AddPersonalDataScanScreen() {
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation<BackLocationStateType>();
  const {data: reservation} = useReservation();
  const isMounted = useIsMounted();
  const timeoutRef = React.useRef<any>();
  const {displayError, ErrorModal, closeErrorModal} = useErrorModal();
  const {data: locations} = useSWR(getURL(api.locations.ENDPOINTS.get('ordering=name')));
  const [documentCheckData, setDocumentCheckData] = React.useState<any>(null);
  const [screenshot, setScreenshot] = React.useState('');
  const [detectedData, setDetectedData] = React.useState<any>(null);
  const [failedAttempts, setFailedAttempts] = React.useState(0);
  const isMaxFailedAttemptsReached =
    failedAttempts >= MAX_SCAN_FAILURE_ATTEMPTS_BEFORE_FORM_PROCEED;
  const {abortController, setupAbortController} = useAbortController();
  const {
    isOpen: isSuccessModalOpen,
    openModal: openSuccessModal,
    closeModal: closeSuccessModal,
  } = useModalControls();
  const {
    isOpen: isLoading,
    openModal: startLoading,
    closeModal: stopLoading,
  } = useModalControls();
  const {
    isOpen: isReviewModalOpen,
    openModal: openReviewModal,
    closeModal: closeReviewModal,
  } = useModalControls();

  const getPersistedState = (): BackLocationStateType => {
    return {
      ...(location?.state || {}),
      [LocationStates.backSideDocumentPhoto]: {
        ...documentCheckData,
        documentCheckId: documentCheckData?.id,
        documentPhoto: screenshot,
      },
    };
  };

  const getValidName = (name?: string) => {
    if (!name) {
      return undefined;
    }
    return PATTERNS.name.test(name) ? name : undefined;
  };

  const buildFormData = () => {
    if (!detectedData) {
      displayError({
        message: t('form_data_is_missing'),
      });
      return;
    }

    const birthDate =
      detectedData?.valid_date_of_birth && detectedData?.date_of_birth
        ? new Date(detectedData.date_of_birth)
        : undefined;
    const docDateOfIssue =
      detectedData?.valid_expiration_date && detectedData?.expiration_date
        ? subYears(new Date(detectedData.expiration_date), 10)
        : undefined;

    const sex = GENDERS_OPTIONS().find(g => g.value === detectedData?.sex);
    const nationality = getLocationsAsOptions(locations).find(l => {
      return l?.value === detectedData?.nationality;
    });

    const remappedDocType = getRemappedDocType({
      docType: detectedData?.type_doc,
      countryCode: getHousingCountryCode(reservation),
    });
    const docType = getDocTypes(reservation, detectedData?.nationality).find(o => {
      return o?.value === remappedDocType;
    });

    return {
      sex,
      docDateOfIssue,
      birthDate,
      nationality,
      docType,
      residenceCountry: nationality,
      docNumber: detectedData?.number,
      name: getValidName(detectedData?.names),
      surname: getValidName(detectedData?.first_surname),
      second_surname: getValidName(detectedData?.second_surname),
    };
  };

  const goToNextStepWithFormData = () => {
    const formData = buildFormData();
    const persistedState = getPersistedState() || {};

    closeReviewModal();
    history.push(PATHS.verificationSelfie, {
      ...persistedState,
      formData,
      highlightFormFields: true,
      ocrWasUsed: true,
    });
  };

  const goToNextStep = () => {
    const persistedState = getPersistedState() || {};

    closeErrorModal();
    history.push(PATHS.verificationSelfie, persistedState);
  };

  const handleDetectionSuccess = (data: any) => {
    setDetectedData(data);
    stopLoading();
    openSuccessModal();

    timeoutRef.current = setTimeout(() => {
      closeSuccessModal();
      setDocumentCheckData(data);
      openReviewModal();
    }, 1000 * SUCCESS_MODAL_TIMEOUT_S);
  };

  const handleDetectionError = (error: any = null, aborted = false) => {
    stopLoading();
    if (!aborted) {
      setFailedAttempts(prevState => prevState + 1);
      displayError(error);
    }
  };

  const checkMRZDetection = async (id: string) => {
    if (!id) {
      handleDetectionError({
        message: t('mrz_check_id_is_missing'),
      });
      return;
    }

    const {data, error, aborted} = await api.ocr.checkImageMRZDetection(id, {
      signal: setupAbortController(),
    });

    if (!isMounted.current) {
      return;
    }

    if (error) {
      handleDetectionError(error, aborted);
      return;
    }

    if (data?.status === DETECTING_STATUSES.completed) {
      if (data?.mrz_detected && data?.valid_composite) {
        handleDetectionSuccess(data);
      } else {
        handleDetectionError({
          message: t('could_not_detect'),
        });
      }

      return;
    }

    if (data?.status === DETECTING_STATUSES.error) {
      handleDetectionError({
        message: t('could_not_detect'),
      });
      return;
    }

    return new Promise(resolve => {
      timeoutRef.current = setTimeout(() => {
        resolve(checkMRZDetection(id));
      }, 1000 * DETECTING_RESPONSES_GAP_S);
    });
  };

  const startMRZDetection = async (image = '', reservationId = '') => {
    setDetectedData(null);
    startLoading();
    const {data, error, aborted} = await api.ocr.sendImageForMRZDetection(
      image,
      reservationId,
      {
        signal: setupAbortController(),
      },
    );

    if (!isMounted.current) {
      return;
    }

    if (error) {
      handleDetectionError(error, aborted);
      return;
    }

    if (data?.status === DETECTING_STATUSES.completed) {
      if (data?.mrz_detected && data?.valid_composite) {
        handleDetectionSuccess(data);
      } else {
        handleDetectionError({
          message: t('could_not_detect'),
        });
      }

      return;
    }

    if (data?.status === DETECTING_STATUSES.error) {
      handleDetectionError({
        message: t('could_not_detect'),
      });
      return;
    }

    await checkMRZDetection(data?.id);
  };

  const startValidation = async (screenshot: any) => {
    try {
      if (!screenshot) return;
      setScreenshot(screenshot);
      const reservationId = reservation?.id;
      await startMRZDetection(screenshot, reservationId);
    } catch (err) {
      displayError(err);
    }
  };

  return (
    <>
      <ConfirmIdentityTemplate
        detailsText={t('now_we_take_back_photo')}
        stateName={LocationStates.backSideDocumentPhoto}
        onSubmit={startValidation}
      />

      <ErrorModal
        message={
          isMaxFailedAttemptsReached ? t('scanner_cannot_detect_try_to_add_manually') : ''
        }
      >
        {isMaxFailedAttemptsReached && (
          <>
            <ErrorModalFirstButton
              label={t('add_data_manually')}
              onClick={goToNextStep}
              icon={<ContractIcon src={contractIcon} alt="Contract" />}
            />
            <ModalButton
              label={t('ok')}
              icon={<img src={likeIcon} alt="Like" />}
              onClick={closeErrorModal}
            />
          </>
        )}
      </ErrorModal>
      <Modal
        open={isSuccessModalOpen}
        title={`${t('success')}!`}
        iconSrc={successIcon}
        iconAlt="Success"
      />
      <Modal
        open={isLoading}
        title={t('scanning')}
        text={t('it_could_take_seconds')}
        iconSrc={scanningIcon}
        iconAlt="Scanner"
      >
        <ModalSecondaryButton onClick={() => abortController.abort()}>
          {t('cancel')}
        </ModalSecondaryButton>
      </Modal>
      <Modal
        open={isReviewModalOpen}
        iconSrc={reviewDataIcon}
        iconAlt="A person with magnifier"
        title={t('please_review_your_data')}
        text={t('be_sure_that_data_correct')}
      >
        <ModalButton
          onClick={goToNextStepWithFormData}
          label={t('next_step')}
          icon={<EyeIcon src={eyeIcon} alt="Eye" />}
        />
      </Modal>
    </>
  );
}

export {AddPersonalDataScanScreen};
